import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { DocumentStatus } from '../types/DocumentationTypes';
import { log } from '../utils/logger';
import FormResponseContext from './FormResponseContext';
import { GlobalProviderContext } from './GlobalProviderContext';
export var FormStatus;
(function (FormStatus) {
    FormStatus["NOT_CREATED"] = "not created";
    FormStatus["NOT_STARTED"] = "not started";
    FormStatus["IN_PROGRESS"] = "in progress";
    FormStatus["COMPLETED"] = "completed";
    FormStatus["PENDING_EIPD"] = "pendingEipd";
})(FormStatus || (FormStatus = {}));
export const FormProgressContext = createContext(null);
export const FormProgressContextProvider = ({ children, sectionId = 'formStart', formStatus = FormStatus.NOT_STARTED, sectionsData, }) => {
    const [currentSectionId, setCurrentSectionId] = useState(sectionId);
    const [editableQuestionId, setEditableQuestionId] = useState(null);
    const [formProgressStatus, setFormProgressStatus] = useState(formStatus);
    const [completedSectionsIds, setCompletedSectionsIds] = useState([]);
    // Importamos el estado compartido de la documentación
    const { documentStatus } = useContext(GlobalProviderContext);
    const { getFormCompletionDate, formResponses } = useContext(FormResponseContext);
    const getCurrentSectionId = () => currentSectionId;
    const saveCurrentSectionId = (sectionId) => setCurrentSectionId(sectionId);
    const getFormProgressStatus = () => formProgressStatus;
    const saveFormProgressStatus = (status) => setFormProgressStatus(status);
    const getCompletedSectionsIds = () => completedSectionsIds;
    const saveCompletedSectionsIds = (sections) => setCompletedSectionsIds(sections);
    const getSectionsData = () => sectionsData;
    const getEditableQuestionId = () => editableQuestionId;
    const saveEditableQuestionId = (contentId) => setEditableQuestionId(contentId);
    useEffect(() => {
        var _a;
        const status = getFormCompletionDate() !== undefined && getFormCompletionDate() !== null
            ? FormStatus.COMPLETED
            : (formResponses === null || formResponses === void 0 ? void 0 : formResponses.length) > 0
                ? FormStatus.IN_PROGRESS
                : FormStatus.NOT_STARTED;
        setFormProgressStatus(status);
        log.debug('form progress use effect.', {
            status,
            formCompletionDate: getFormCompletionDate(),
            formResponses: formResponses === null || formResponses === void 0 ? void 0 : formResponses.length,
            documentStatus,
            completedSectionsIds,
            currentSectionId,
        });
        // Si el estado de la documentación pasa a GENERATING, ejecutamos la lógica deseada
        if (documentStatus !== DocumentStatus.NOT_STARTED) {
            // Aquí puedes agregar acciones específicas, por ejemplo, ajustar currentSectionId o disparar otros procesos.
            setFormProgressStatus(FormStatus.COMPLETED);
        }
        if (completedSectionsIds.length === 0 && currentSectionId === 'formStart' && (formResponses === null || formResponses === void 0 ? void 0 : formResponses.length) > 0) {
            log.debug('Computando secciones completadas');
            const sectionsIds = sectionsData.map((section) => ({ id: section.id, parent: section.parent }));
            const responsesIds = [...new Set(formResponses.map((r) => r.contentId.split('-')[0]))];
            let previouslyCompletedSectionsIds = [];
            responsesIds.forEach((responseId) => {
                sectionsIds.forEach((section) => {
                    if (section.id === responseId && section.parent !== undefined) {
                        previouslyCompletedSectionsIds.push(responseId, section.parent);
                    }
                    else {
                        previouslyCompletedSectionsIds.push(responseId);
                    }
                });
            });
            previouslyCompletedSectionsIds = [...new Set(previouslyCompletedSectionsIds)];
            previouslyCompletedSectionsIds = previouslyCompletedSectionsIds.sort((a, b) => {
                var _a, _b;
                const aNum = parseFloat(((_a = a.match(/\d+/)) === null || _a === void 0 ? void 0 : _a[0]) || '0');
                const bNum = parseFloat(((_b = b.match(/\d+/)) === null || _b === void 0 ? void 0 : _b[0]) || '0');
                if (aNum !== bNum) {
                    return aNum - bNum;
                }
                return a.length - b.length;
            });
            if (status !== FormStatus.COMPLETED) {
                const idOfLastSectionInProgress = previouslyCompletedSectionsIds[previouslyCompletedSectionsIds.length - 1];
                setCurrentSectionId(idOfLastSectionInProgress);
                const lastSectionInProgressParent = (_a = sectionsIds.find((section) => section.id === idOfLastSectionInProgress)) === null || _a === void 0 ? void 0 : _a.parent;
                previouslyCompletedSectionsIds = previouslyCompletedSectionsIds.filter((sectionId) => {
                    if (lastSectionInProgressParent !== undefined) {
                        return sectionId !== idOfLastSectionInProgress && sectionId !== lastSectionInProgressParent;
                    }
                    else {
                        return sectionId !== idOfLastSectionInProgress;
                    }
                });
            }
            setCompletedSectionsIds(previouslyCompletedSectionsIds);
        }
    }, [
        completedSectionsIds,
        currentSectionId,
        formResponses,
        getFormCompletionDate,
        sectionsData,
        documentStatus,
    ]);
    const value = {
        formProgressStatus,
        getSectionsData,
        getCurrentSectionId,
        saveCurrentSectionId,
        getFormProgressStatus,
        saveFormProgressStatus,
        getCompletedSectionsIds,
        saveCompletedSectionsIds,
        getEditableQuestionId,
        saveEditableQuestionId,
    };
    return _jsx(FormProgressContext.Provider, Object.assign({ value: value }, { children: children }));
};
export default FormProgressContext;
